import { Link } from "react-router-dom";
import { HelperMode, HelperModeChips, helperModeLabel, helperModeOf } from "./helperMode";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { asLengthUnit, canComplete, gajGirah, LENGTH_UNITS, LengthUnit, unusualDay } from "./sareeLength";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  changeLoomPeople,
  closeLoom,
  finishSaree,
  changeSareeDesign,
  getSareeHistory,
  getSareeNumbers,
  previewLoomEntry,
  reopenLoom,
  saveLoomEntry,
  resolveRate,
  checkSareeLength,
  setSareeLength,
  startSaree,
  changeSareeStartDate,
  saveQuickRate,
  linkLoomRegister,
  listRegisteredLooms,
  saveLoomPhoto,
  tagLoomLocation,
  voidLoomEntry,
} from "../../../services/karkhanaApi";
import { uploadLoomPhoto } from "../../../services/geoLoomApi";
import { isKarkhanaSupervisor, isOwnerOrManager } from "../../../helpers/karkhanaEnabled";
import { DesignPicker } from "../KarkhanaRateCardsPage";
import { fmtDay, partName, rupees, todayIso } from "../rates/rateModel";
import { dayLabel, daysBetween } from "../daily/dailyModel";
import { enqueue, newRequestId } from "./entryQueue";
import { currentPosition, Fix, mapLink } from "./geo";
import VoiceNote from "../voice/VoiceNote";
import { useKT } from "../i18n/kt";
import PersonPicker, { Person } from "./PersonPicker";
import { confirmDialog, askReason } from "../ui/dialog";
import SareeLabelAttach from "../sarees/SareeLabelAttach";
import QuickAddPerson from "../people/QuickAddPerson";
import SareeStartChange from "../sarees/SareeStartChange";
import { backfillBody, backfillOffer, backfillPart, moveStartOffer, peopleToConfirm } from "../sarees/sareeEditModel";
import { kp } from "../karkhanaPaths";
import { DraftLine, draftScope, draftSlot, pageSlot, partAlreadySaved, patchDraftRequest, readDraft, rowSlot, savedPartsByLoom, writeDraft } from "../daily/dailyDraft";
import { isRemoved } from "../sarees/entryChangeModel";
import { OutLink } from "../ui/leaveKarkhana";
import { CoveringTag } from "./myRound";

type Mode = "entry" | "start" | "close" | "finish" | "check";

export const CLOSE_REASONS: { key: string; label: string }[] = [
  { key: "WEAVER_ABSENT", label: "Weaver absent" },
  { key: "NO_YARN", label: "No yarn" },
  { key: "REPAIR", label: "Repair" },
  { key: "VACANT", label: "Vacant" },
  { key: "NO_MEASUREMENT", label: "Couldn't measure" },
  { key: "OTHER", label: "Other" },
];

/**
 * The loom's entry in the studio's loom register (the Looms / geotag pages): geotag and photos can be
 * added from the floor at any time; nothing in the rounds waits for them.
 */
const LoomRegister = ({
  loom,
  busy,
  staff,
  onTag,
  onPhoto,
  onLink,
}: {
  loom: any;
  busy: boolean;
  staff: boolean;
  onTag: () => void;
  onPhoto: (file: File, view: string) => void;
  onLink: (geoLoomId: string) => void;
}) => {
  const { t } = useKT();
  const reg = loom.register;
  const loc = loom.location;
  const photoRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState("FRONT");
  const [candidates, setCandidates] = useState<any[] | null>(null);
  const status = reg?.geoTagStatus;
  const views: string[] = reg?.photoViews || [];
  const openLink = async () => {
    const res: any = await listRegisteredLooms().catch(() => null);
    setCandidates(res?.success ? res.data || [] : []);
  };
  return (
    <div className="kr-register">
      <div className="kr-register__row">
        <span>
          <strong>{t("Loom register")}</strong>
          {reg?.loomCode ? <span className="kw-muted"> · {reg.loomCode}</span> : null}
        </span>
        <span className={`kw-chip${status === "VERIFIED" ? " is-ok" : status === "PENDING" ? " is-info" : ""}`}>
          {status === "VERIFIED" ? t("Geotag verified") : status === "PENDING" ? t("Geotagged") : t("Not geotagged")}
        </span>
      </div>
      {loc ? (
        <div className="kw-muted">
          Location{loc.accuracyMeters ? ` ±${Math.round(loc.accuracyMeters)} m` : ""} ·{" "}
          <a href={mapLink(loc.lat, loc.lng)} target="_blank" rel="noopener noreferrer">
            {t("Map")}
          </a>
        </div>
      ) : null}
      {reg?.photoUrl ? <img className="kr-register__photo" src={reg.photoUrl} alt={`Loom ${loom.code}`} /> : null}
      <div className="kr-chips" style={{ marginTop: 8 }}>
        <button type="button" className="kr-choice" disabled={busy} onClick={onTag}>
          {loc ? t("Tag again here") : t("Tag location here")}
        </button>
        <button type="button" className="kr-choice" disabled={busy} onClick={() => photoRef.current?.click()}>
          {views.includes(view) ? t("Retake {{side}} photo", { side: t(view.toLowerCase()) }) : t("Add {{side}} photo", { side: t(view.toLowerCase()) })}
        </button>
        <select className="form-select form-select-sm kr-register__view" value={view} onChange={(e) => setView(e.target.value)} aria-label="Photo side">
          {["FRONT", "BACK", "LEFT", "RIGHT"].map((v) => (
            <option key={v} value={v}>
              {v.charAt(0) + v.slice(1).toLowerCase()}
              {views.includes(v) ? " ✓" : ""}
            </option>
          ))}
        </select>
        <input
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPhoto(f, view);
            e.target.value = "";
          }}
        />
      </div>
      {staff && !reg?.photoUrl && status !== "PENDING" && status !== "VERIFIED" ? (
        candidates === null ? (
          <button type="button" className="btn btn-link btn-sm p-0 mt-2" onClick={openLink}>
            {t("Already registered on the Looms page? Link it")}
          </button>
        ) : candidates.length ? (
          <div className="mt-2">
            <span className="kr-label">{t("Link {{code}} to", { code: loom.code })}</span>
            {candidates.map((c) => (
              <button key={c.id} type="button" className="kr-choice" style={{ marginRight: 6, marginBottom: 6 }} disabled={busy} onClick={() => onLink(c.id)}>
                {c.loomCode}
                {c.loomGroup ? ` · ${c.loomGroup}` : ""}
                {c.giName ? ` · ${c.giName}` : ""}
              </button>
            ))}
          </div>
        ) : (
          <div className="kw-muted mt-2">{t("No other registered looms to link.")}</div>
        )
      ) : null}
    </div>
  );
};

/** Entered length of the saree, its full length and the last check-in, with a warning if they disagree. */
const SareeProgressLine = ({ progress, result, onCheck }: { progress: any; result: any; onCheck: () => void }) => {
  const { t } = useKT();
  const p = result || progress;
  const last = p?.lastCheckIn;
  const inches = (v?: string) => (v === undefined || v === null ? "" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })} in`);
  return (
    <div className={`kr-progress${last?.status === "ENTERED_LESS" || last?.status === "ENTERED_MORE" ? " is-warn" : ""}`}>
      <div className="kr-pay__row">
        <span>
          {t("So far {{in}} in · {{gg}}", {
            in: Number(p?.enteredInches || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 }),
            gg: gajGirah(Number(p?.enteredInches || 0), Number(p?.inchesPerGirah) || 2.6875),
          })}
          {p?.fullInches ? ` ${t("of {{in}} in", { in: Math.round(Number(p.fullInches)) })}` : ""}
        </span>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={onCheck}>
          {t("Check length")}
        </button>
      </div>
      {last ? (
        <div className="kw-muted">
          {fmtDay(last.date)}: {t("measured {{m}} m ({{in}}) vs {{e}} entered", { m: last.measuredMetres, in: inches(last.measuredInches), e: inches(last.enteredInches) })} —{" "}
          {last.status === "MATCHES"
            ? t("matches ✓")
            : last.status === "ENTERED_LESS"
            ? t("{{d}} less entered. An entry may be missing.", { d: inches(String(Math.abs(Number(last.enteredInches) - Number(last.measuredInches)))) })
            : t("{{d}} more entered than woven. Check before paying.", { d: inches(String(Math.abs(Number(last.enteredInches) - Number(last.measuredInches)))) })}
        </div>
      ) : null}
    </div>
  );
};

/** Geotag state beside the loom number: amber when the loom isn't tagged yet; tapping opens the register. */
const GeoChip = ({ status, onOpen }: { status?: string; onOpen: () => void }) => {
  const { t } = useKT();
  const label = status === "VERIFIED" ? t("Geotag verified") : status === "PENDING" ? t("Tagged") : t("Not tagged");
  return (
    <button
      type="button"
      className={`kr-geochip${status === "VERIFIED" ? " is-ok" : status === "PENDING" ? " is-quiet" : " is-warn"}`}
      onClick={onOpen}
      aria-label={`${label} · ${t("Loom location and photo")}`}
      title={label}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
      </svg>
      {status === "VERIFIED" ? null : <span>{label}</span>}
    </button>
  );
};

/**
 * The last 5 days of work on this saree, newest first, with a running total of its length: the top row's
 * total is the saree's length so far. Only saree parts that are counted (not removed or rejected) add length,
 * as the server's "so far" does. The full history is one tap away. No money here.
 */
const SareeSoFar = ({ pieceId, sareeNumber, reload, addsLength }: { pieceId: string; sareeNumber: string; reload: number; addsLength: (workTypeId?: string) => boolean }) => {
  const { t, lang } = useKT();
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [days, setDays] = useState<any[]>([]);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    let live = true;
    setStatus("loading");
    Promise.resolve()
      .then(() => getSareeHistory(pieceId))
      .then((res: any) => {
        if (!live) return;
        if (res?.success) {
          setDays(res.data?.days || []);
          setStatus("ok");
        } else setStatus("error");
      })
      .catch(() => live && setStatus("error"));
    return () => {
      live = false;
    };
  }, [pieceId, reload, tries]);
  const recent = useMemo(() => {
    // Oldest first to add up, keeping the server's order within a day; then newest first to show.
    const counts = (d: any) => !isRemoved(d) && d.status !== "REJECTED" && addsLength(d.workTypeId);
    let total = 0;
    const rows = days
      .map((d, i) => ({ d, i }))
      .sort((x, y) => (x.d.date < y.d.date ? -1 : x.d.date > y.d.date ? 1 : x.i - y.i))
      .map(({ d }) => {
        const counted = counts(d);
        if (counted) total += Number(d.inches) || 0;
        return { d, counted, total };
      })
      .reverse();
    const dates = new Set(Array.from(new Set(rows.map((r) => String(r.d.date)))).slice(0, 5));
    return rows.filter((r) => dates.has(String(r.d.date)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  const inches = (n: number) => `${Math.round(n * 10) / 10} in`;
  const dayShort = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" });
  return (
    <section className="kr-sofar" aria-label={t("Saree {{n}} so far", { n: sareeNumber })}>
      <span className="kr-label">{t("Saree {{n}} so far", { n: sareeNumber })}</span>
      {status === "error" ? (
        <div className="kw-muted">
          {t("Couldn't load the entries.")}{" "}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setTries((n) => n + 1)}>
            {t("Try again")}
          </button>
        </div>
      ) : status === "loading" && !days.length ? (
        <div className="kw-muted">{t("Loading entries…")}</div>
      ) : !recent.length ? (
        <div className="kw-muted">{t("No entries yet")}</div>
      ) : (
        <ul className="kr-sofar__list">
          <li className="kr-sofar__head" aria-hidden="true">
            <span>{t("Day")}</span>
            <span>{t("Part")}</span>
            <span className="kr-sofar__in">{t("Woven")}</span>
            <span>{t("Karigar")}</span>
            <span className="kr-sofar__total">{t("Total")}</span>
          </li>
          {recent.map(({ d, counted, total }) => {
            const gone = isRemoved(d);
            return (
              <li key={d.id} className={gone ? "is-removed" : undefined}>
                <span className="kr-sofar__day">{dayShort(d.date)}</span>
                <span className="kw-code">{d.workTypeCode}</span>
                <span className="kr-sofar__in">{d.inches} in</span>
                <span className="kw-code">{d.weaver?.code || d.weaverCode || "—"}</span>
                <span className="kr-sofar__total" title={counted ? undefined : t("Doesn't add to the saree's length")}>{counted ? inches(total) : "—"}</span>
                {!gone && (d.waitingApproval || d.status === "PENDING_APPROVAL") ? <span className="kw-chip is-review">{t("waiting")}</span> : null}
                {gone ? <span className="kw-chip">{t("removed")}</span> : null}
              </li>
            );
          })}
        </ul>
      )}
      <Link className="kr-sofar__all" to={kp.saree(pieceId)}>
        {t("See all")} ›
      </Link>
    </section>
  );
};

/** One more part woven the same day ("+ Add another part"): saved as its own entry with the same people. */
type PartRow = { key: number; requestId: string; workTypeId: string; inches: string; unit: LengthUnit; sool: boolean };

const ExtraPartRow = ({
  row,
  n,
  types,
  taken,
  previewBody,
  odd,
  error,
  onChange,
  onRemove,
  disabled = false,
}: {
  disabled?: boolean;
  row: PartRow;
  n: number;
  types: any[];
  taken: (workTypeId: string, rowKey: number) => boolean;
  previewBody: any;
  odd: string;
  error?: string;
  onChange: (patch: Partial<PartRow>) => void;
  onRemove: () => void;
}) => {
  const { t, lang } = useKT();
  const [calc, setCalc] = useState<any>(null);
  const key = JSON.stringify(previewBody);
  useEffect(() => {
    if (!row.inches || !row.workTypeId) {
      setCalc(null);
      return;
    }
    const h = window.setTimeout(async () => {
      try {
        const res: any = await previewLoomEntry(previewBody);
        setCalc(res?.success ? res.data.calc : null);
      } catch {
        setCalc(null);
      }
    }, 300);
    return () => window.clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (
    <div className="kr-partrow" role="group" aria-label={t("Part {{n}}", { n })}>
      <div className="kr-partrow__head">
        <span className="kr-label">{t("Part {{n}}", { n })}</span>
        <button type="button" className="kr-iconbtn" disabled={disabled} onClick={onRemove} aria-label={t("Remove part {{n}}", { n })}>
          ✕
        </button>
      </div>
      <select className={`kr-select${row.workTypeId ? "" : " is-needed"}`} disabled={disabled} value={row.workTypeId} onChange={(e) => onChange({ workTypeId: e.target.value })} aria-label={t("Part {{n}} being woven", { n })}>
        {!row.workTypeId ? <option value="">{t("Choose the part")}</option> : null}
        {types.map((w) => (
          <option key={w.id} value={w.id} disabled={w.id !== row.workTypeId && taken(w.id, row.key)}>
            {partName(w, lang, t)} ({w.code})
          </option>
        ))}
      </select>
      <div className="kr-inches">
        <input
          disabled={disabled}
          value={row.inches}
          onChange={(e) => onChange({ inches: e.target.value.replace(/[^\d.]/g, "") })}
          inputMode="decimal"
          enterKeyHint="done"
          placeholder="0"
          aria-label={t("Woven today, part {{n}}", { n })}
        />
        <div className="kr-unit-toggle" role="radiogroup" aria-label="Unit">
          {LENGTH_UNITS.map(({ unit: u, label }) => (
            <button key={u} type="button" role="radio" aria-checked={row.unit === u} disabled={disabled} className={`kr-choice${row.unit === u ? " is-on" : ""}`} onClick={() => onChange({ unit: u })}>
              {t(label)}
            </button>
          ))}
        </div>
      </div>
      <label className="kr-label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input type="checkbox" checked={row.sool} disabled={disabled} onChange={(e) => onChange({ sool: e.target.checked })} />
        {t("Woven with ladka sool")}
      </label>
      {odd ? (
        <div className="kr-odd" role="alert">
          {odd}
        </div>
      ) : null}
      {error ? (
        <div className="kr-pay is-missing" role="alert">
          {t("Not saved")}: {error}
        </div>
      ) : null}
      <PayBox calc={calc} inches={row.inches} sool={row.sool} />
    </div>
  );
};

const readRole = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const reasonLabel = (k?: string) => CLOSE_REASONS.find((r) => r.key === k)?.label || k || "";

const isNetworkError = (e: unknown) => e instanceof TypeError;

/** Works out ₹ for one loom card; the server's preview is the official number. */
const PayBox = ({ calc, inches, sool = false }: { calc: any; inches: string; sool?: boolean }) => {
  const { t } = useKT();
  if (!calc || !inches) return null;
  if (calc.status !== "OK") {
    return <div className="kr-pay is-missing">{t("No approved rate for this design and work type on this day. The entry is still saved and flagged.")}</div>;
  }
  const aloneShown = calc.aloneAllowance && Number(calc.aloneAllowance) > 0;
  return (
    <div className="kr-pay" aria-live="polite">
      {sool && !calc.ladkaSoolPerInch ? <div className="kw-muted">{t("This part has no ladka sool rate on the rate card, so nothing extra is paid.")}</div> : null}
      <div className="kr-pay__row">
        <span>
          {t("Weaver")} · {inches} in × ₹{calc.weaverRatePerInch}
        </span>
        <span>{rupees(calc.weaverAmount)}</span>
      </div>
      {aloneShown ? (
        <div className="kr-pay__row">
          <span>{t("Alone allowance")}</span>
          <span>{rupees(calc.aloneAllowance)}</span>
        </div>
      ) : null}
      {calc.ladkaSoolAmount && Number(calc.ladkaSoolAmount) > 0 ? (
        <div className="kr-pay__row">
          <span>
            {t("Ladka sool")} · ₹{calc.ladkaSoolPerInch}/in
          </span>
          <span>{rupees(calc.ladkaSoolAmount)}</span>
        </div>
      ) : null}
      {calc.bonusPercent ? (
        <div className="kr-pay__row">
          <span>
            {t("Incentive")} {calc.incentiveMet ? `(${calc.bonusPercent}%)` : `— ${t("needs {{n}} in today", { n: Number(calc.dailyTargetInches) * Number(calc.cutoffPercent) / 100 })}`}
          </span>
          <span>{calc.incentiveMet ? rupees(calc.incentiveAmount) : "—"}</span>
        </div>
      ) : null}
      <div className="kr-pay__row" style={{ marginTop: 6 }}>
        <span>{t("Total")}</span>
        <span className="kr-pay__total">{rupees(calc.total)}</span>
      </div>
    </div>
  );
};

type LoomRef = { id: string; code: string };

const LoomSheet = ({
  loom,
  date,
  late,
  workTypes,
  orgId,
  onClose,
  onSaved,
  asPage = false,
  prevLoom = null,
  nextLoom = null,
  onGoTo,
}: {
  /** Full page (Rounds) instead of a panel over the list. */
  asPage?: boolean;
  prevLoom?: LoomRef | null;
  nextLoom?: LoomRef | null;
  onGoTo?: (id: string) => void;
  loom: any;
  date: string;
  late: boolean;
  workTypes: any[];
  orgId: string;
  onClose: () => void;
  /** advance = move on to the next pending loom. */
  onSaved: (opts: { advance: boolean; queued?: boolean }) => void;
}) => {
  const { t, lang } = useKT();
  // On a past day the loom offers the saree that's on it now; "a different saree" shows the start form.
  const [forceNew, setForceNew] = useState(false);
  // A day before a later saree's start: filled in like a running loom; saving also moves the start
  // (owner/manager) or sends entry + move to Approvals (supervisor). Same rule as the Daily work rows.
  const backfill = backfillOffer(loom);
  const filling = !!backfill && !forceNew;
  const a = loom.assignment || (filling ? loom.laterSaree : null);
  const shortDay = (iso?: string) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" }) : "");
  const [mode, setMode] = useState<Mode>(a ? "entry" : "start");
  const [busy, setBusy] = useState(false);
  // Entry
  // A back-filled saree starts on its own part, or none (picked by hand): never the previous saree's part.
  const [workTypeId, setWorkTypeId] = useState<string>(backfill ? backfillPart(loom) : loom.lastWorkTypeId || workTypes[0]?.id || "");
  const [inches, setInches] = useState("");
  const [unit, setUnit] = useState<LengthUnit>("INCH");
  const [partway, setPartway] = useState(false);
  const [alone, setAlone] = useState("");
  const [calc, setCalc] = useState<any>(null);
  // Woven with ladka sool: the rate card's ladka sool extra is paid on these inches.
  const [sool, setSool] = useState(false);
  // Weaver and helper are never carried over silently: confirmed ("same as last time") or changed first.
  const [peopleOk, setPeopleOk] = useState(false);
  const requestId = useRef(newRequestId());
  // Start / people. The saree number comes from the studio's counter (1, 2, 3… unless the office
  // types the number on the saree's tag, e.g. one already on the loom before HKP).
  const [nextSaree, setNextSaree] = useState<number | null | undefined>(undefined);
  const staff = !isKarkhanaSupervisor(readRole());
  // Only the owner approves rates, so only they see the optional rate while starting a saree.
  const owner = readRole() === "ORGANIZATION_ADMIN";
  const [startOn, setStartOn] = useState(date);
  const [earlier, setEarlier] = useState(false);
  const [numberText, setNumberText] = useState("");
  // Staff type the number on a saree's tag; supervisors too while the studio onboards (the server says).
  const [typeNumber, setTypeNumber] = useState(staff);
  const [rate, setRate] = useState("");
  const [rateUnit, setRateUnit] = useState<"INCH" | "GIRAH">("INCH");
  // The design's approved rate, filled in for the owner; saved again only if they change it.
  const [rateFrom, setRateFrom] = useState<{ rate: string; unit: "INCH" | "GIRAH" } | null>(null);
  // Length woven on the sheet's day, saved as the saree's first entry with the start (one tap).
  const [startLength, setStartLength] = useState("");
  const [startUnit, setStartUnit] = useState<LengthUnit>("INCH");
  // A new person typed into a search box: added here, without leaving the loom.
  const [adding, setAdding] = useState<null | { target: "weaver" | "helper" | "dayWeaver" | "dayHelper"; name: string }>(null);
  // Just saved (one line per entry, each with its own Undo) until the next loom.
  const [lastSaved, setLastSaved] = useState<{ id: string; text: string; who: string }[]>([]);
  // More parts woven the same day, under the first: each one saves as its own entry.
  const [extra, setExtra] = useState<PartRow[]>([]);
  // A part that didn't save keeps its reason here (by request id) until the next Save.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  // Today's people card: open (or folded to one line once confirmed), and which change is being made.
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [peopleStep, setPeopleStep] = useState<"" | "choose" | "weaver" | "helper">("");
  // After a change: keep it from tomorrow too ("From now on") or just for today (the default).
  const [keepAsk, setKeepAsk] = useState<null | { name: string; role: string; mode?: boolean }>(null);
  // Today's people kept through the reload that "From now on" causes (it would reset them to last time's).
  // Cleared on every run of the people effect, so it can never confirm people later or on another saree.
  const keepDay = useRef<null | { assignmentId?: string; weaver: Person | null; helper: Person | null; mode: HelperMode }>(null);
  const [geoOpen, setGeoOpen] = useState(false);
  // Bumped after a save or undo here: the saree's history list loads again.
  const [historyTick, setHistoryTick] = useState(0);
  useEffect(() => {
    setLastSaved([]);
    setExtra([]);
    setRowErrors({});
    setPeopleOpen(false);
    setPeopleStep("");
    setKeepAsk(null);
    keepDay.current = null;
    setForceNew(false);
    setPeopleOk(false);
    setSool(false);
    setStartOn(date);
    setEarlier(false);
    setRate("");
  }, [loom.id, date]);
  const [design, setDesign] = useState<{ id: string; number: string } | null>(null);
  const [weaver, setWeaver] = useState<Person | null>(a?.weaver || null);
  const [helper, setHelper] = useState<Person | null>(a?.helper || null);
  const [startMode, setStartMode] = useState<HelperMode>(helperModeOf(a?.helperMode));
  const alonePerson = startMode !== "WITH_HELPER";
  // Who works today: starts from whoever wove this saree last time (else the loom's usual people),
  // and can be changed for just this entry. Changing the loom's usual people is "Change people".
  const last = loom.lastPeople || null;
  const [dayWeaver, setDayWeaver] = useState<Person | null>(last?.weaver || a?.weaver || null);
  const [dayHelper, setDayHelper] = useState<Person | null>(last ? last.helper || null : a?.helper || null);
  const [dayMode, setDayMode] = useState<HelperMode>(helperModeOf(last ? last.helperMode : a?.helperMode));
  const dayAlone = dayMode !== "WITH_HELPER";
  const [editWho, setEditWho] = useState<"" | "design">("");
  const [shortBy, setShortBy] = useState<{ entered: number; full: number } | null>(null);
  // A saree started (or people changed) while this loom is open: start today's people from them.
  useEffect(() => {
    // "From now on" just made today's people the loom's usual ones: keep them, still confirmed.
    const kept = keepDay.current;
    keepDay.current = null;
    if (
      kept &&
      kept.assignmentId === a?.id &&
      kept.weaver?.id === a?.weaver?.id &&
      kept.mode === helperModeOf(a?.helperMode) &&
      (kept.mode !== "WITH_HELPER" || kept.helper?.id === a?.helper?.id)
    ) {
      setDayWeaver(kept.weaver);
      setDayHelper(kept.helper);
      setDayMode(kept.mode);
      setPeopleOk(true);
      return;
    }
    setDayWeaver(last?.weaver || a?.weaver || null);
    setDayHelper(last ? last.helper || null : a?.helper || null);
    setDayMode(helperModeOf(last ? last.helperMode : a?.helperMode));
    // New saree or new people from the server: today's people must be confirmed again.
    setPeopleOk(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id, a?.weaver?.id, a?.helper?.id, a?.helperMode, last?.weaver?.id]);
  // Close / finish
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [finishAs, setFinishAs] = useState("COMPLETED");
  // Length check-in ("2.5 m done")
  const [length, setLength] = useState("");
  const [lengthUnit, setLengthUnit] = useState("M");
  const [isFullLength, setIsFullLength] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);
  // The saree's progress came back from the server (after a save, undo or check): it wins over a check
  // made earlier on this screen, so Complete never stays locked on an old measurement.
  const progressKey = JSON.stringify(a?.progress || null);
  useEffect(() => {
    setCheckResult(null);
  }, [progressKey]);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);
  const inchesRef = useRef<HTMLInputElement>(null);
  // An odd-looking day (past the saree's length, or far more than a day) saves on the second tap.
  const [oddSeen, setOddSeen] = useState("");

  // Typed and not saved: kept on this phone in the same per-day copy as the Daily work list, so a
  // reload, leaving the loom or a dropped connection never loses it.
  const [scope] = useState(() => draftScope());
  const [draftNote, setDraftNote] = useState<"" | "restored" | "dropped">("");
  const prevInches = useRef("");
  useEffect(() => {
    setDraftNote("");
    prevInches.current = "";
    if (!scope || !a) return;
    const draft = readDraft(scope, date);
    const savedParts = savedPartsByLoom([loom]).get(String(loom.id));
    // The server has this part for the loom that day, or another saree is on it now: keep the server's,
    // say so. A different part saved that day is a second entry: the copy is restored.
    const stale = (l: DraftLine) => partAlreadySaved(l, savedParts) || String(l.sareeNumber) !== String(a.sareeNumber);
    const known = [...(draft?.people || []), a.weaver, a.helper].filter(Boolean) as Person[];
    const person = (id?: string): Person | null => (id ? known.find((p) => p.id === id) || { id, code: "", name: "" } : null);
    const restorePeople = (l: DraftLine) => {
      setDayWeaver(person(l.weaverId));
      setDayHelper(l.helperId ? person(l.helperId) : null);
      setDayMode(l.helperId ? "WITH_HELPER" : l.helperMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE");
      setPeopleOk(!!l.confirmed);
    };
    // Added part rows ("+ Add another part") have their own slots.
    const rowLines = (draft?.lines || []).filter((l) => l.loomId === loom.id && l.row);
    const keepRows = rowLines.filter((l) => !stale(l));
    rowLines.filter(stale).forEach((l) => patchDraftRequest(scope, date, loom.id, l.requestId, null));
    if (keepRows.length) {
      setExtra(keepRows.map((l) => ({ key: Number(l.row), requestId: l.requestId, workTypeId: l.workTypeId || "", inches: l.inches, unit: asLengthUnit(l.unit), sool: !!l.sool })));
    }
    // Its own girah / alone / ladka sool line first, else the row shared with the Daily work list.
    const d = draft?.lines.find((l) => draftSlot(l) === pageSlot(loom.id)) || draft?.lines.find((l) => draftSlot(l) === loom.id);
    if (!d || stale(d)) {
      if (d) patchDraftRequest(scope, date, loom.id, d.requestId, null);
      if (keepRows.length) {
        restorePeople(keepRows[0]);
        setDraftNote("restored");
      } else if (d || rowLines.length) setDraftNote("dropped");
      return;
    }
    setInches(d.inches);
    if (d.workTypeId) setWorkTypeId(d.workTypeId);
    setUnit(asLengthUnit(d.unit));
    setPartway(!!(d.alone && d.alone.trim()));
    setAlone(d.alone || "");
    setSool(!!d.sool);
    restorePeople(d);
    if (d.requestId) requestId.current = d.requestId;
    setDraftNote("restored");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loom.id, date]);

  // Every change goes to the copy at once (one small line); emptied inches remove it.
  useEffect(() => {
    if (!scope || mode !== "entry" || !a || draftScope() !== scope) return;
    const had = prevInches.current;
    prevInches.current = inches;
    if (!inches.trim()) {
      if (had.trim()) patchDraftRequest(scope, date, loom.id, requestId.current, null);
      return;
    }
    const people = [dayWeaver, dayAlone ? null : dayHelper].filter(Boolean).map((p) => ({ id: p!.id, code: p!.code, name: p!.name || "" }));
    const line = {
          loomId: loom.id,
          sareeNumber: a.sareeNumber,
          weaverId: dayWeaver?.id || "",
          helperId: dayAlone ? "" : dayHelper?.id || "",
          helperMode: dayMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
          workTypeId,
          inches,
          requestId: requestId.current,
          status: "EDIT",
          confirmed: peopleOk,
          unit,
          alone: partway ? alone : undefined,
          sool,
        } as const;
    // This work moving between slots (girah ↔ inches): its old line goes; another screen's work stays.
    const moved = (readDraft(scope, date)?.lines || [])
      .filter((l) => l.loomId === loom.id && l.requestId === requestId.current && draftSlot(l) !== draftSlot(line))
      .map(draftSlot);
    writeDraft(scope, date, [line], people, moved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inches, workTypeId, dayWeaver?.id, dayHelper?.id, dayMode, peopleOk, unit, alone, partway, sool, mode]);

  // Added part rows go to the same copy, each in its own slot; an emptied row's slot is removed.
  useEffect(() => {
    if (!scope || mode !== "entry" || !a || draftScope() !== scope) return;
    const people = [dayWeaver, dayAlone ? null : dayHelper].filter(Boolean).map((p) => ({ id: p!.id, code: p!.code, name: p!.name || "" }));
    const lines: DraftLine[] = extra
      .filter((r) => r.inches.trim())
      .map((r) => ({
        loomId: loom.id,
        sareeNumber: a.sareeNumber,
        weaverId: dayWeaver?.id || "",
        helperId: dayAlone ? "" : dayHelper?.id || "",
        helperMode: dayMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
        workTypeId: r.workTypeId,
        inches: r.inches,
        requestId: r.requestId,
        // A row that failed to save keeps its reason in the copy, like a red row in the Daily work list.
        status: rowErrors[r.requestId] ? "ERROR" : "EDIT",
        message: rowErrors[r.requestId] || undefined,
        confirmed: peopleOk,
        unit: r.unit,
        sool: r.sool,
        row: r.key,
      }));
    const empty = extra.filter((r) => !r.inches.trim()).map((r) => rowSlot(loom.id, r.key));
    if (lines.length || empty.length) writeDraft(scope, date, lines, people, empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra, dayWeaver?.id, dayHelper?.id, dayMode, peopleOk, mode, rowErrors]);

  const discardDraft = () => {
    if (scope) patchDraftRequest(scope, date, loom.id, requestId.current, null);
    if (scope) extra.forEach((r) => patchDraftRequest(scope, date, loom.id, r.requestId, null));
    setExtra([]);
    setRowErrors({});
    prevInches.current = "";
    setInches("");
    setAlone("");
    setPartway(false);
    setSool(false);
    setPeopleOk(false);
    requestId.current = newRequestId();
    setDraftNote("");
  };

  const visibleTypes = useMemo(() => workTypes.filter((w) => w.active !== false), [workTypes]);
  // A new studio's saree parts arrive after the loom opens: pick the part once they're there.
  useEffect(() => {
    if (!workTypeId && visibleTypes.length && !backfill) setWorkTypeId(loom.lastWorkTypeId || visibleTypes[0].id);
  }, [workTypeId, visibleTypes, loom.lastWorkTypeId, backfill]);

  useEffect(() => {
    if (mode === "entry") inchesRef.current?.focus();
  }, [mode, a?.id]);

  // Live pay preview while typing, debounced. The server works out the money.
  useEffect(() => {
    if (mode !== "entry" || !inches || !workTypeId || !a) {
      setCalc(null);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        const res: any = await previewLoomEntry(body());
        setCalc(res?.success ? res.data.calc : null);
      } catch {
        setCalc(null);
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inches, unit, workTypeId, partway, alone, mode, dayWeaver?.id, dayHelper?.id, dayAlone, sool]);

  /** One entry's request: the work of one part row, with today's people, date and loom. */
  const bodyFor = (r: { workTypeId: string; inches: string; unit: LengthUnit; sool: boolean; alone?: string }) => ({
    loomId: loom.id,
    date,
    weaverId: dayWeaver?.id,
    helperId: dayAlone ? "" : dayHelper?.id,
    helperMode: dayMode,
    workTypeId: r.workTypeId,
    inches: r.inches.trim(),
    unit: r.unit,
    inchesAlone: r.alone !== undefined ? r.alone.trim() || "0" : "",
    ...(r.sool ? { ladkaSool: true } : {}),
    ...(late && voiceNote ? { voiceNoteUrl: voiceNote } : {}),
    // Shown as closed and saved anyway: that reopens it. Closed by someone after this screen opened: the server asks first.
    ...(loom.state === "CLOSED" ? { reopen: true } : {}),
    ...(filling ? backfillBody(backfill) : {}),
  });
  const body = () => bodyFor({ workTypeId, inches, unit, sool, alone: partway ? alone : undefined });

  /** Empty the inches after a save; the part stays, ready for the next entry. */
  const clearEntry = () => {
    setInches("");
    setAlone("");
    setPartway(false);
    setSool(false);
    setCalc(null);
    setOddSeen("");
    requestId.current = newRequestId();
  };

  // Only saree work counts toward the saree's length (the server's total is SAREE entries only).
  const isSareeWork = (id: string) => visibleTypes.find((w) => w.id === id)?.category === "SAREE";
  const oddFor = (v: string, u: LengthUnit, part: string) =>
    mode === "entry" && a && isSareeWork(part) ? unusualDay(Number(v), u, a.progress, Number(a.progress?.inchesPerGirah) || 2.6875) : null;
  const oddText = (o: ReturnType<typeof unusualDay>) =>
    !o
      ? ""
      : o.kind === "PAST_FULL"
      ? t("That makes {{after}} in on a {{full}} in saree. Check the number.", { after: Math.round(o.after), full: Math.round(o.full) })
      : t("{{today}} in is far more than one day's weaving. Check the number.", { today: Math.round(o.today) });
  const odd = oddFor(inches, unit, workTypeId);
  // Every odd-looking row at once: Save again keeps them all, a change to any of them asks again.
  const oddKey = [odd ? `0:${inches}|${unit}` : "", ...extra.map((r) => (oddFor(r.inches, r.unit, r.workTypeId) ? `${r.key}:${r.inches}|${r.unit}` : ""))].filter(Boolean).join(",");

  // Parts the server already has for today's weaver on this loom and day. The server allows one entry
  // per loom, day, part and weaver: a substitute karigar may weave the same part the same day.
  const savedFor = (weaverId?: string) =>
    new Set<string>(
      (loom.entries || [])
        .filter((e: any) => e.status !== "VOID" && e.status !== "REJECTED" && !e.removed && weaverId && e.weaverId === weaverId)
        .map((e: any) => String(e.workTypeId || ""))
        .filter(Boolean)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const savedToday = useMemo(() => savedFor(dayWeaver?.id), [loom, dayWeaver?.id]);
  /** A part already in another row on this screen, or already saved for today's weaver. */
  const partTaken = (id: string, rowKey: number) =>
    savedToday.has(id) || (rowKey !== 0 && workTypeId === id) || extra.some((r) => r.key !== rowKey && r.workTypeId === id);
  const addRow = () =>
    setExtra((xs) => {
      const key = Math.max(0, ...xs.map((x) => x.key)) + 1;
      const used = new Set([workTypeId, ...xs.map((x) => x.workTypeId), ...Array.from(savedToday)]);
      const part = visibleTypes.find((w) => !used.has(w.id))?.id || "";
      return [...xs, { key, requestId: newRequestId(), workTypeId: part, inches: "", unit: "INCH", sool: false }];
    });
  // Opening on a part today's weaver already has (usually "same as last time"): start on the next free part.
  useEffect(() => {
    if (!workTypeId || inches.trim() || !savedToday.has(workTypeId)) return;
    const used = new Set([...extra.map((x) => x.workTypeId), ...Array.from(savedToday)]);
    const next = visibleTypes.find((w) => !used.has(w.id));
    if (next) setWorkTypeId(next.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workTypeId, savedToday]);
  const patchRow = (key: number, patch: Partial<PartRow>) => setExtra((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const removeRow = (r: PartRow) => {
    if (scope) patchDraftRequest(scope, date, loom.id, r.requestId, null);
    setExtra((xs) => xs.filter((x) => x.key !== r.key));
  };

  const save = async (advance: boolean) => {
    // Row 1 (the part being woven) and any added part rows: every filled row is its own entry.
    const rows = [
      { key: 0, requestId: requestId.current, workTypeId, inches, unit, sool, alone: partway ? alone : undefined },
      ...extra.map((r) => ({ ...r, alone: undefined as string | undefined })),
    ];
    const filled = rows.filter((r) => r.inches.trim());
    if (!filled.length || filled.some((r) => !(Number(r.inches) > 0))) {
      notifications({ title: "Error", message: t("Enter the inches woven"), type: "danger" });
      return;
    }
    if (filled.some((r) => !r.workTypeId)) {
      notifications({ title: t("Choose the part"), message: t("Which part? Pick it before saving."), type: "warning" });
      return;
    }
    const partCode = (id: string) => visibleTypes.find((x) => x.id === id)?.code || "";
    const twice = filled.find((r, i) => filled.findIndex((x) => x.workTypeId === r.workTypeId) !== i);
    if (twice) {
      notifications({ title: t("Part entered twice"), message: t("{{part}} is in two rows. Pick another part for one of them.", { part: partCode(twice.workTypeId) }), type: "warning" });
      return;
    }
    const already = filled.find((r) => savedToday.has(r.workTypeId));
    if (already) {
      notifications({
        title: t("Part entered twice"),
        message: t("{{who}} already has {{part}} on this day. Pick another part.", { who: dayWeaver?.code || "", part: partCode(already.workTypeId) }),
        type: "warning",
      });
      return;
    }
    if (!peopleOk) {
      notifications({ title: t("Confirm the people"), message: t("Confirm today's weaver and helper, or change them, before saving."), type: "warning" });
      return;
    }
    if (!dayWeaver || (!dayAlone && !dayHelper)) {
      notifications({ title: "Error", message: t("Choose the weaver and helper"), type: "danger" });
      return;
    }
    if (partway && Number(alone) > Number(inches)) {
      notifications({ title: "Error", message: t("Inches alone can't be more than the total"), type: "danger" });
      return;
    }
    if (oddKey && oddSeen !== oddKey) {
      setOddSeen(oddKey);
      notifications({ title: t("Check the inches"), message: t("Tap Save again to keep it."), type: "warning" });
      return;
    }
    setBusy(true);
    setRowErrors({});
    const who = [dayWeaver?.name, dayAlone ? "" : dayHelper?.name].filter(Boolean).join(" + ");
    const saved: { id: string; text: string; who: string }[] = [];
    const errors: Record<string, string> = {};
    const gone = new Set<number>();
    let queued = false;
    let forApproval = false;
    let failMessage = "";
    try {
      for (const r of filled) {
        const payload = { ...bodyFor(r), clientRequestId: r.requestId };
        const code = visibleTypes.find((w) => w.id === r.workTypeId)?.code || "";
        try {
          const res: any = await saveLoomEntry(payload);
          if (!res?.success) {
            // Kept on screen and on this phone with the reason, like a red row in the Daily work list.
            const msg = t(res?.message || "Check the entry");
            errors[r.requestId] = msg;
            failMessage = failMessage || msg;
            if (scope) patchDraftRequest(scope, date, loom.id, r.requestId, { status: "ERROR", message: msg });
            continue;
          }
          if (scope) patchDraftRequest(scope, date, loom.id, r.requestId, null);
          gone.add(r.key);
          if (res.data.status === "PENDING_APPROVAL") forApproval = true;
          saved.push({
            id: res.data.id,
            text: `${filled.length > 1 && code ? `${code} ` : ""}${res.data.inches} in${res.data.calc?.total ? ` · ${rupees(res.data.calc.total)}` : ""}`,
            who,
          });
        } catch (e) {
          if (isNetworkError(e)) {
            // No signal: keep it on the phone; it is sent when the connection is back (same request id).
            enqueue(orgId, { clientRequestId: r.requestId, body: payload, loomCode: loom.code, queuedAt: new Date().toISOString() });
            if (scope) patchDraftRequest(scope, date, loom.id, r.requestId, null);
            gone.add(r.key);
            queued = true;
          } else {
            const msg = t("Something went wrong");
            errors[r.requestId] = msg;
            failMessage = failMessage || msg;
          }
        }
      }
    } finally {
      setBusy(false);
    }
    setRowErrors(errors);
    // Saved rows clear; a row that failed stays with its reason. A second part woven the same day is a
    // new entry: never resend a saved row's request id.
    if (gone.has(0)) {
      clearEntry();
      // Row 1 moves on to the next part today's weaver doesn't have yet (else stays on its part, empty).
      const taken = new Set([
        ...Array.from(savedToday),
        ...filled.filter((r) => gone.has(r.key)).map((r) => r.workTypeId),
        ...extra.filter((x) => !gone.has(x.key)).map((x) => x.workTypeId),
      ]);
      const at = visibleTypes.findIndex((w) => w.id === workTypeId);
      const order = [...visibleTypes.slice(at + 1), ...visibleTypes.slice(0, at + 1)];
      const next = order.find((w) => !taken.has(w.id));
      if (next) setWorkTypeId(next.id);
    }
    if (gone.size) setExtra((xs) => xs.filter((x) => !gone.has(x.key)));
    if (failMessage) notifications({ title: t("Not saved"), message: failMessage, type: "danger" });
    if (!gone.size) return;
    setDraftNote("");
    if (saved.length) {
      notifications({
        title: forApproval ? t("Saved for approval") : t("Saved"),
        message: `${loom.code} · ${saved.map((x) => x.text).join(", ")}`,
        type: "success",
      });
      // Stays on screen until the next loom, so nobody types the same inches twice; a retry adds to it.
      setLastSaved((xs) => [...xs, ...saved]);
    }
    if (queued) notifications({ title: t("Saved on this phone"), message: t("{{code}} will be sent when there is signal", { code: loom.code }), type: "warning" });
    setHistoryTick((n) => n + 1);
    // An earlier length check is older than this work: the saree's progress decides from here.
    setCheckResult(null);
    // A row that didn't save keeps the loom open, so it isn't left behind.
    onSaved({ advance: advance && !failMessage, ...(queued ? { queued: true } : {}) });
  };

  const tagHere = async () => {
    setBusy(true);
    let fix: Fix;
    try {
      fix = await currentPosition();
    } catch (e: any) {
      notifications({ title: t("No location"), message: t(e?.message || "Couldn't get a location"), type: "warning" });
      setBusy(false);
      return;
    }
    setBusy(false);
    if (await run(() => tagLoomLocation(loom.id, fix), `${loom.code} tagged here`)) onSaved({ advance: false });
  };

  const addPhoto = async (file: File, view: string) => {
    setBusy(true);
    let url: string;
    let accuracy: number | null = null;
    try {
      url = await uploadLoomPhoto(file);
      accuracy = (await currentPosition().catch(() => null))?.accuracyMeters ?? null;
    } catch (e: any) {
      notifications({ title: t("Photo not saved"), message: t(e?.message || "Could not upload the photo"), type: "danger" });
      setBusy(false);
      return;
    }
    setBusy(false);
    if (await run(() => saveLoomPhoto(loom.id, { photoUrl: url, photoView: view, accuracyMeters: accuracy }), `${loom.code} photo saved`)) {
      onSaved({ advance: false });
    }
  };

  const linkRegister = async (geoLoomId: string) => {
    if (await run(() => linkLoomRegister(loom.id, geoLoomId), `${loom.code} linked to the register`)) onSaved({ advance: false });
  };

  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try {
      const res: any = await fn();
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return false;
      }
      notifications({ title: t("Done"), message: ok, type: "success" });
      return true;
    } catch {
      notifications({ title: t("No signal"), message: t("This needs a connection. Try again in a moment."), type: "danger" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const peopleBody = () => ({
    weaverId: weaver?.id,
    helperId: alonePerson ? undefined : helper?.id,
    helperMode: startMode,
    date,
  });

  useEffect(() => {
    if (mode !== "start") return;
    getSareeNumbers()
      .then((res: any) => {
        // The next number is only the placeholder: typing must never add to it (1 + 18301 → 118301).
        setNextSaree(res?.success ? res.data?.next ?? 1 : null);
        if (res?.success && typeof res.data?.canTypeNumber === "boolean") setTypeNumber(res.data.canTypeNumber);
        setNumberText("");
      })
      .catch(() => setNextSaree(null));
  }, [mode]);

  /** The part a new saree starts on: the loom's last part, else S_1, else the first. */
  const startPart = () =>
    visibleTypes.find((w) => w.id === loom.lastWorkTypeId) || visibleTypes.find((w) => w.code === "S_1") || visibleTypes[0];

  // Owner starting a saree: bring in the design's approved weaver rate for that part and day.
  const designId = (design as { id?: string } | null)?.id;
  useEffect(() => {
    if (mode !== "start" || !owner || !designId) return;
    const part = startPart();
    if (!part) return;
    let live = true;
    resolveRate(designId, part.id, startOn)
      .then((res: any) => {
        if (!live) return;
        const line = res?.success && res.data?.status === "OK" ? res.data.line : null;
        const typed = line ? String(line.sourceExpression || line.weaverRatePerInch || "") : "";
        if (typed) {
          const u = line.sourceUnit === "GIRAH" ? "GIRAH" : "INCH";
          setRate(typed);
          setRateUnit(u);
          setRateFrom({ rate: typed, unit: u });
        } else {
          // No rate yet: a rate brought in for another design goes; the owner's own typing stays.
          setRateFrom((was) => {
            if (was) setRate("");
            return null;
          });
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, owner, designId, startOn]);

  const submitStart = async () => {
    // The office may type the number on the saree's tag; otherwise the next number is given.
    const typed = numberText.trim();
    const own = typeNumber && typed && typed !== String(nextSaree ?? "") ? typed : "";
    const missing = !design ? t("Choose the design") : !weaver ? t("Choose the weaver") : !alonePerson && !helper ? t("Choose the helper, or Weaver alone") : "";
    if (startLength.trim() && !(Number(startLength) > 0)) {
      notifications({ title: t("Error"), message: t("Enter the length woven, or leave it empty"), type: "danger" });
      return;
    }
    if ((own && !/^\d{1,9}$/.test(own)) || missing) {
      notifications({
        title: t("Error"),
        message: own && !/^\d{1,9}$/.test(own) ? t("A saree number is digits only, e.g. 14500") : missing,
        type: "danger",
      });
      return;
    }
    const chosen = design as { id: string; number: string };
    let started = "";
    const ok = await run(async () => {
      const res: any = await startSaree(loom.id, { ...peopleBody(), date: startOn, ...(own ? { sareeNumber: own } : {}), designId: chosen.id });
      started = res?.data?.sareeNumber || "";
      return res;
    }, t("Saree started on loom {{code}}", { code: loom.code }));
    const sameAsCard = !!rateFrom && rate.trim() === rateFrom.rate && rateUnit === rateFrom.unit;
    if (ok && owner && rate.trim() && !sameAsCard) {
      // The weaver's rate for the part being woven, applying from the saree's first day.
      const part = startPart();
      try {
        const r: any = await saveQuickRate({ designId: chosen.id, workTypeId: part?.id, rate: rate.trim(), rateUnit, effectiveFrom: startOn });
        if (!r?.success) notifications({ title: t("Rate not saved"), message: t(r?.message || "Set it later in Pay"), type: "warning" });
      } catch {
        notifications({ title: t("Rate not saved"), message: t("Set it later in Pay"), type: "warning" });
      }
    }
    // The length woven on this sheet's day, as the saree's first entry.
    let lengthLeft = "";
    if (ok && startLength.trim()) {
      const part = startPart();
      try {
        const r: any = await saveLoomEntry({
          ...peopleBody(),
          loomId: loom.id,
          workTypeId: part?.id,
          inches: startLength.trim(),
          unit: startUnit,
          inchesAlone: "",
          clientRequestId: newRequestId(),
        });
        if (r?.success) {
          notifications({ title: t("Saved"), message: `${loom.code} · ${r.data?.inches} in${r.data?.calc?.total ? ` · ${rupees(r.data.calc.total)}` : ""}`, type: "success" });
        } else {
          lengthLeft = r?.message || "Check the entry";
        }
      } catch {
        lengthLeft = "Something went wrong";
      }
    }
    if (ok) {
      notifications({ title: `${t("Saree")} ${started}`, message: `${t("Write {{n}} on the saree's tag", { n: started })}. ${t("Paste an HKP label on the loom and scan it from More for this loom")}`, type: "info" });
      // A fresh saree starts with an empty entry form; a length that didn't save waits in it.
      if (lengthLeft) {
        notifications({ title: t("Length not saved"), message: `${t(lengthLeft)}. ${t("It is in the box below: check it and Save.")}`, type: "warning" });
      }
      setInches(lengthLeft ? startLength.trim() : "");
      setUnit(lengthLeft ? startUnit : "INCH");
      setStartLength("");
      setAlone("");
      setPartway(false);
      setCalc(null);
      requestId.current = newRequestId();
      setMode("entry");
      onSaved({ advance: false });
    }
  };

  /** One tap when a saree comes off the loom; the next saree starts with the same people and design. */
  const completeSaree = async () => {
    if (!a) return;
    // A complete saree is about full length (5 gaj, or its measured length). Short: look again first.
    const len = canComplete(checkResult || a.progress);
    if (!len.ok) {
      setShortBy({ entered: len.entered, full: len.full });
      return;
    }
    if (!(await confirmDialog({ title: t("Saree {{n}} is complete on {{day}}?", { n: a.sareeNumber, day: fmtDay(date) }) }))) return;
    if (await run(() => finishSaree(loom.id, { finishStatus: "COMPLETED", date }), t("Saree {{n}} complete", { n: a.sareeNumber }))) {
      if (a.designId) setDesign({ id: a.designId, number: a.designNumber });
      setMode("start");
      onSaved({ advance: false });
    }
  };

  /** The saree was tagged with the wrong design: the number stays, the design (and pay) is corrected. */
  const changeDesign = async (d: { id: string; number: string } | null) => {
    if (!a || !d || d.id === a.designId) {
      setEditWho("");
      return;
    }
    const n = entries.length;
    const sure = (await confirmDialog({ title: n
        ? t("Change saree {{s}} from design {{from}} to {{to}}? Pay for its entries is worked out again with {{to}}'s rates.", { s: a.sareeNumber, from: a.designNumber, to: d.number })
        : t("Change saree {{s}} from design {{from}} to {{to}}?", { s: a.sareeNumber, from: a.designNumber, to: d.number }) }));
    if (!sure) return;
    if (await run(() => changeSareeDesign(loom.id, d.id), t("Design changed to {{to}}", { to: d.number }))) {
      setEditWho("");
      onSaved({ advance: false });
    }
  };

  /** Today's people chosen in the card: confirmed, folded, and asked whether to keep them from tomorrow. */
  const pickDay = (role: "weaver" | "helper", p: Person) => {
    if (role === "weaver") setDayWeaver(p);
    else setDayHelper(p);
    setPeopleOk(true);
    setPeopleStep("");
    setPeopleOpen(false);
    setKeepAsk({ name: [p.code, p.name].filter(Boolean).join(" "), role: role === "weaver" ? "karigar" : "jodiya" });
  };
  const pickDayMode = (m: HelperMode) => {
    setDayMode(m);
    if (m === "WITH_HELPER") {
      // A jodiya is needed now: confirmed once one is picked.
      setPeopleOk(false);
      return;
    }
    setPeopleOk(true);
    setPeopleStep("");
    setPeopleOpen(false);
    setKeepAsk({ name: t(helperModeLabel(m)), role: "", mode: true });
  };

  /** "From now on": today's people become the loom's usual people (the old "Change people"). */
  const keepFromNow = async () => {
    if (!dayWeaver || (!dayAlone && !dayHelper)) return;
    keepDay.current = { assignmentId: a?.id, weaver: dayWeaver, helper: dayAlone ? null : dayHelper, mode: dayMode };
    const ok = await run(
      () => changeLoomPeople(loom.id, { weaverId: dayWeaver.id, helperId: dayAlone ? undefined : dayHelper?.id, helperMode: dayMode, date }),
      t("People updated")
    );
    if (ok) {
      setKeepAsk(null);
      onSaved({ advance: false });
    } else keepDay.current = null;
  };

  const submitClose = async () => {
    if (!reason) {
      notifications({ title: "Error", message: t("Choose why the loom is closed"), type: "danger" });
      return;
    }
    if (await run(() => closeLoom(loom.id, { reason, note, date, ...(late && voiceNote ? { voiceNoteUrl: voiceNote } : {}) }), `${loom.code} closed`)) onSaved({ advance: true });
  };

  const submitFinish = async () => {
    if (await run(() => finishSaree(loom.id, { finishStatus: finishAs, date }), "Saree finished")) {
      if (a?.designId) setDesign({ id: a.designId, number: a.designNumber });
      setMode("start");
      onSaved({ advance: false });
    }
  };

  const submitCheck = async () => {
    if (!length.trim()) {
      notifications({ title: "Error", message: t("Enter the length"), type: "danger" });
      return;
    }
    setBusy(true);
    try {
      const body = { length: length.trim(), lengthUnit, date };
      const res: any = isFullLength ? await setSareeLength(loom.id, body) : await checkSareeLength(loom.id, body);
      if (!res?.success) {
        notifications({ title: "Error", message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      setCheckResult(isFullLength ? null : res.data);
      setLength("");
      setIsFullLength(false);
      setMode("entry");
      onSaved({ advance: false });
    } catch {
      notifications({ title: t("No signal"), message: t("This needs a connection. Try again in a moment."), type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (e: any) => {
    const why = (await askReason({ title: t("Remove {{what}} from {{code}}? Say why:", { what: `${e.workTypeCode} ${e.inches} in`, code: loom.code }) }));
    if (!why || !why.trim()) return;
    if (await run(() => voidLoomEntry(e.id, { note: why.trim() }), "Entry removed")) onSaved({ advance: false });
  };

  const reopen = async () => {
    if (await run(() => reopenLoom(loom.id, { date }), `${loom.code} reopened`)) onSaved({ advance: false });
  };

  const entries = (loom.entries || []).filter((e: any) => e.status !== "VOID");
  // The loom has no saree on this day but one from a later day: move that saree's start back (one tap).
  const offer = moveStartOffer(loom);
  // Only the owner or a manager moves a start (offer.canMove); the floor is told to ask the office.
  const moveStart = async () => {
    if (!offer?.canMove || !offer.pieceId) return;
    const n = loom.laterSaree?.sareeNumber;
    const ok = await run(() => changeSareeStartDate(offer.pieceId as string, date), t("Saree {{n}} counts from {{day}}", { n, day: fmtDay(date) }));
    if (ok) onSaved({ advance: false });
  };
  const toConfirm = peopleToConfirm(loom);
  const canCorrect = isOwnerOrManager(readRole());
  // Today's people differ from the loom's usual (last time's) people.
  const peopleChanged =
    (dayWeaver?.id || "") !== (toConfirm.weaver?.id || "") ||
    dayMode !== helperModeOf(toConfirm.helperMode) ||
    (!dayAlone && (dayHelper?.id || "") !== (toConfirm.helper?.id || ""));
  const len = a ? canComplete(checkResult || a.progress) : null;

  return (
    <>
      {asPage ? null : <div className="kr-sheet-backdrop" onClick={onClose} />}
      <section
        className={`kr-sheet${asPage ? " is-page" : ""}`}
        role={asPage ? "region" : "dialog"}
        aria-modal={asPage ? undefined : true}
        aria-label={`Loom ${loom.code}`}
      >
        <header className="kr-sheet__head">
          {asPage ? (
            <button
              type="button"
              className="kh-iconbtn"
              disabled={!prevLoom}
              onClick={() => prevLoom && onGoTo?.(prevLoom.id)}
              aria-label={prevLoom ? `${t("Previous loom")} ${prevLoom.code}` : t("Previous loom")}
              title={prevLoom?.code}
            >
              ‹
            </button>
          ) : null}
          {/* Figma: "Loom 1" and under it the day (when it isn't today) or the saree on it. */}
          <div className="kr-sheet__title">
            <span className="kr-sheet__titlerow">
              <h3>
                {t("Loom")} {loom.code}
              </h3>
              <GeoChip status={loom.register?.geoTagStatus} onOpen={() => setGeoOpen(true)} />
            </span>
            <small>
              {date !== todayIso()
                ? `${dayLabel(date, lang)} · ${t(daysBetween(date, todayIso()) === 1 ? "yesterday" : "{{n}} days old", { n: daysBetween(date, todayIso()) })}`
                : a
                  ? `${t("Saree")} ${a.sareeNumber} · ${a.designNumber || ""}`
                  : t("No saree yet")}
            </small>
          </div>
          {loom.state === "CLOSED" ? <span className="kw-chip">{t("Closed")} · {t(reasonLabel(loom.closure?.reason))}</span> : null}
          {asPage ? (
            <button
              type="button"
              className="kh-iconbtn"
              disabled={!nextLoom}
              onClick={() => nextLoom && onGoTo?.(nextLoom.id)}
              aria-label={nextLoom ? `${t("Next loom")} ${nextLoom.code}` : t("Next loom")}
              title={nextLoom?.code}
            >
              ›
            </button>
          ) : (
            <button type="button" className="kr-iconbtn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </header>

        <div className="kr-sheet__body">
          {late ? <div className="kr-banner is-warn">{t("This day is 3 or more days back. Entries wait for the admin's approval.")}</div> : null}
          {late && (mode === "entry" || mode === "close") ? <VoiceNote value={voiceNote} onChange={setVoiceNote} /> : null}

          {a && mode === "entry"
            ? lastSaved.map((x) => (
                <div key={x.id} className="kr-saved" role="status">
                  <span>
                    <strong>✓ {t("Saved")}: {x.text}</strong>
                    {x.who ? <small>{x.who}</small> : null}
                  </span>
                  <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={async () => {
                    if (await run(() => voidLoomEntry(x.id, { note: "Undo right after saving" }), t("Removed"))) {
                      setLastSaved((xs) => xs.filter((y) => y.id !== x.id));
                      setHistoryTick((n) => n + 1);
                      setCheckResult(null);
                      onSaved({ advance: false });
                    }
                  }}>
                    {t("Undo")}
                  </button>
                </div>
              ))
            : null}
          {draftNote === "restored" && mode === "entry" ? (
            <div className="kr-draftnote" role="status">
              {t("Restored from this phone")} ·{" "}
              <button type="button" className="btn btn-link btn-sm p-0" onClick={discardDraft}>
                {t("Discard")}
              </button>
            </div>
          ) : draftNote === "dropped" ? (
            <div className="kr-draftnote" role="status">
              {t("This part is already saved for this day, or another saree is on the loom; this phone's copy dropped.")}
            </div>
          ) : null}
          {/* In entry mode the saree and people sit together in the "today" card below. */}
          {a && mode !== "start" && mode !== "entry" ? (
            <dl className="kr-who">
              <dt>{t("Saree")}</dt>
              <dd>
                <span className="kw-code">{a.sareeNumber}</span> · {a.designNumber}
              </dd>
              {a.productId ? (
                <>
                  {/* Made as a draft when the saree started; the studio finishes it on the product page. */}
                  <dt>{t("Product")}</dt>
                  <dd>
                    {staff ? (
                      <OutLink className="kr-who__pick kr-product" to={`/product/${a.productId}`}>
                        {t("Draft")}
                        <span className="kr-who__change">{t("Open")} ›</span>
                      </OutLink>
                    ) : (
                      t("Draft made")
                    )}
                  </dd>
                </>
              ) : null}
              <dt>{t("Weaver")}</dt>
                  <dd>
                    <span className="kw-code">{a.weaver?.code}</span> {a.weaver?.name}
                  </dd>
                  <dt>{t("Helper")}</dt>
                  <dd>
                    {a.helperMode === "WITH_HELPER" ? (
                      <>
                        <span className="kw-code">{a.helper?.code}</span> {a.helper?.name}
                      </>
                    ) : (
                      t(helperModeLabel(a.helperMode))
                    )}
                  </dd>
            </dl>
          ) : null}
          {filling && mode === "entry" ? (
            <div className="kr-startmove" role="note">
              <strong>
                {t("Saree {{n}} · starts {{from}} → will start {{to}}", { n: a.sareeNumber, from: shortDay(backfill?.currentStart), to: shortDay(date) })}
              </strong>
              {backfill?.needsApproval ? <span>{t("Saving sends the work and the start move to Approvals.")}</span> : null}
            </div>
          ) : null}
          {a && mode === "entry" ? (
            // One card for today: loom, day, saree, karigar and jodiya, and the people check.
            <div
              className={`kr-today${!peopleOk && toConfirm.helperChanged ? " is-warn" : ""}${peopleOk && !peopleOpen ? " is-ok" : ""}`}
              role="group"
              aria-label={t("Today on loom {{code}}", { code: loom.code })}
            >
              <div className="kr-today__head">
                <strong>{t("Loom {{code}}", { code: loom.code })}</strong>
                <span>{fmtDay(date)}</span>
              </div>
              <dl className="kr-today__rows">
                <dt>{t("Saree")}</dt>
                <dd>
                  {!filling ? (
                    <button type="button" className="kr-who__pick" onClick={() => setEditWho(editWho === "design" ? "" : "design")} aria-expanded={editWho === "design"}>
                      <span className="kw-code">{a.sareeNumber}</span> · {a.designNumber}
                      <span className="kr-who__change">{t("Change")}</span>
                    </button>
                  ) : (
                    <>
                      <span className="kw-code">{a.sareeNumber}</span> · {a.designNumber}
                    </>
                  )}
                </dd>
                {a.productId ? (
                  <>
                    <dt>{t("Product")}</dt>
                    <dd>
                      {staff ? (
                        <OutLink className="kr-who__pick kr-product" to={`/product/${a.productId}`}>
                          {t("Draft")}
                          <span className="kr-who__change">{t("Open")} ›</span>
                        </OutLink>
                      ) : (
                        t("Draft made")
                      )}
                    </dd>
                  </>
                ) : null}
                <dt>{t("Karigar")}</dt>
                <dd>
                  <span className="kw-code">{dayWeaver?.code || "—"}</span> {dayWeaver?.name}
                </dd>
                <dt>{t("Jodiya")}</dt>
                <dd>
                  {dayAlone ? (
                    t(helperModeLabel(dayMode))
                  ) : (
                    <>
                      <span className="kw-code">{dayHelper?.code || "—"}</span> {dayHelper?.name}
                    </>
                  )}
                </dd>
              </dl>
              {a.pieceId && !filling ? (
                <SareeStartChange pieceId={a.pieceId} start={a.startedOn || a.from} canChange={canCorrect} onChanged={() => onSaved({ advance: false })} />
              ) : null}
              {peopleOk && !peopleOpen ? (
                <div className="kr-today__ok" role="status">
                  <span>✓ {peopleChanged ? t("Changed today") : t("Same people")}</span>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setPeopleOpen(true); setPeopleStep(""); }}>
                    {t("Change")}
                  </button>
                </div>
              ) : (
                <>
                  {toConfirm.helperChanged ? <small className="kr-people__warn">{t("The helper changed this week. Check before saving.")}</small> : null}
                  {peopleStep === "" ? (
                    <div className="kr-people__actions">
                      <button type="button" className="kr-choice" disabled={!dayWeaver || (!dayAlone && !dayHelper)} onClick={() => { setPeopleOk(true); setPeopleOpen(false); }}>
                        {t("Same people")}
                      </button>
                      <button type="button" className="kr-choice" onClick={() => setPeopleStep("choose")}>
                        {t("Different…")}
                      </button>
                    </div>
                  ) : peopleStep === "choose" ? (
                    <div className="kr-people__actions">
                      <button type="button" className="kr-choice" onClick={() => setPeopleStep("weaver")}>
                        {t("Change karigar")}
                      </button>
                      <button type="button" className="kr-choice" onClick={() => setPeopleStep("helper")}>
                        {t("Change jodiya")}
                      </button>
                    </div>
                  ) : peopleStep === "weaver" ? (
                    <div className="kr-who__edit">
                      <PersonPicker
                        value={null}
                        onChange={(p) => p && pickDay("weaver", p)}
                        placeholder={t("Who wove today? Code or name, e.g. K_56")}
                        label={t("Weaver today")}
                        onAddNew={(name) => setAdding({ target: "dayWeaver", name })}
                        addLabel="as a new weaver"
                      />
                      {a.weaver && dayWeaver?.id !== a.weaver.id ? (
                        <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => pickDay("weaver", a.weaver)}>
                          {t("Back to the usual weaver ({{code}})", { code: a.weaver.code })}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="kr-who__edit">
                      <HelperModeChips value={dayMode} onChange={pickDayMode} />
                      {!dayAlone ? (
                        <PersonPicker
                          value={null}
                          onChange={(p) => p && pickDay("helper", p)}
                          placeholder={t("Who helped today? Code or name, e.g. J_120")}
                          label={t("Helper today")}
                          onAddNew={(name) => setAdding({ target: "dayHelper", name })}
                          addLabel="as a new helper"
                        />
                      ) : null}
                    </div>
                  )}
                  {peopleStep ? (
                    <button type="button" className="btn btn-link btn-sm p-0 align-self-start" onClick={() => setPeopleStep(peopleStep === "choose" ? "" : "choose")}>
                      ‹ {t("Back")}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          {a && mode === "entry" && keepAsk && peopleOk && !peopleOpen && peopleChanged ? (
            <div className="kr-keep" role="group" aria-label={t("Keep from tomorrow?")}>
              <span>
                {keepAsk.mode
                  ? t("Keep “{{mode}}” from tomorrow too?", { mode: keepAsk.name })
                  : t("Keep {{name}} as {{role}} from tomorrow too?", { name: keepAsk.name, role: t(keepAsk.role) })}
              </span>
              <div className="kr-chips">
                <button type="button" className="kr-choice is-on" onClick={() => setKeepAsk(null)}>
                  {t("Just today")}
                </button>
                <button type="button" className="kr-choice" disabled={busy} onClick={keepFromNow}>
                  {t("From now on")}
                </button>
              </div>
            </div>
          ) : null}
          {a && mode === "entry" && editWho === "design" ? (
            <div className="kr-who__edit">
              <p className="kw-muted" style={{ margin: "0 0 6px" }}>
                {t("Saree number {{s}} stays. Pick the right design:", { s: a.sareeNumber })}
              </p>
              <DesignPicker value={null} onChange={changeDesign} autoFocus />
            </div>
          ) : null}
          {a && mode === "entry" ? (
            <SareeProgressLine progress={a.progress} result={checkResult} onCheck={() => setMode("check")} />
          ) : null}

          {mode === "check" && a ? (
            <>
              <span className="kr-label">{isFullLength ? t("Full length of saree {{n}}", { n: a.sareeNumber }) : t("How much of saree {{n}} is woven?", { n: a.sareeNumber })}</span>
              <div className="kr-inches">
                <Input value={length} onChange={(e) => setLength(e.target.value)} inputMode="decimal" placeholder={lengthUnit === "M" ? "e.g. 2.5" : "e.g. 98"} aria-label="Length" autoFocus />
                <div className="kr-unit-toggle" role="radiogroup" aria-label="Unit">
                  {[["M", "m"], ["IN", "inch"], ["GIRAH", "girah"]].map(([k, l]) => (
                    <button key={k} type="button" role="radio" aria-checked={lengthUnit === k} className={`kr-choice${lengthUnit === k ? " is-on" : ""}`} onClick={() => setLengthUnit(k)}>
                      {t(l)}
                    </button>
                  ))}
                </div>
              </div>
              <label className="kw-muted mt-2" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={isFullLength} onChange={(e) => setIsFullLength(e.target.checked)} />
                {t("This is the saree's full length (to show progress), not a measurement")}
              </label>
              <p className="kw-muted mt-2">{t("Measure what's on the loom; HKP compares it with the inches entered for this saree.")}</p>
            </>
          ) : null}

          {loom.state === "CLOSED" && mode === "entry" ? (
            <div className="kr-banner is-info">
              {t("Closed since {{day}} ({{why}}). Saving work below reopens it.", { day: fmtDay(loom.closure?.from), why: t(reasonLabel(loom.closure?.reason)) })}{" "}
              <button type="button" className="btn btn-link btn-sm p-0" onClick={reopen}>
                {t("Reopen without work")}
              </button>
            </div>
          ) : null}

          {mode === "entry" && a ? (
            <>
              {/* The part being woven runs for days: it starts from what was woven last and only
                  needs changing when the weaver moves on to the next part. */}
              <label className="kr-label" htmlFor="kr-worktype">
                {t("Part being woven")}
                {!filling && loom.lastWorkTypeId && workTypeId === loom.lastWorkTypeId ? <span className="kw-muted"> · {t("same as last time")}</span> : null}
              </label>
              <select id="kr-worktype" className={`kr-select${workTypeId ? "" : " is-needed"}`} disabled={busy} value={workTypeId} onChange={(e) => setWorkTypeId(e.target.value)}>
                {!workTypeId ? <option value="">{t("Choose the part")}</option> : null}
                {visibleTypes.map((w) => (
                  <option key={w.id} value={w.id} disabled={w.id !== workTypeId && partTaken(w.id, 0)}>
                    {partName(w, lang, t)} ({w.code})
                  </option>
                ))}
              </select>
              {workTypeId && savedToday.has(workTypeId) && inches.trim() ? (
                <div className="kw-muted">{t("{{who}} already has this part on this day. Pick another part.", { who: dayWeaver?.code || "" })}</div>
              ) : null}
              <span className="kr-label">{t("Woven today")}</span>
              <div className="kr-inches">
                <input
                  ref={inchesRef}
                  disabled={busy}
                  value={inches}
                  onChange={(e) => setInches(e.target.value.replace(/[^\d.]/g, ""))}
                  inputMode="decimal"
                  enterKeyHint="done"
                  placeholder="0"
                  aria-label={unit === "GIRAH" ? "Girah woven" : unit === "CM" ? "Cm woven" : "Inches woven"}
                />
                <div className="kr-unit-toggle" role="radiogroup" aria-label="Unit">
                  {LENGTH_UNITS.map(({ unit: u, label }) => (
                    <button key={u} type="button" role="radio" aria-checked={unit === u} disabled={busy}
                      className={`kr-choice${unit === u ? " is-on" : ""}`} onClick={() => setUnit(u)}>
                      {t(label)}
                    </button>
                  ))}
                </div>
              </div>
              <label className="kr-label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={sool} disabled={busy} onChange={(e) => setSool(e.target.checked)} />
                {t("Woven with ladka sool")}
                <span className="kw-muted">· {t("extra rate, if the rate card has one")}</span>
              </label>
              {a.helperMode === "WITH_HELPER" ? (
                <>
                  <label className="kr-label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="checkbox" checked={partway} disabled={busy} onChange={(e) => setPartway(e.target.checked)} />
                    {t("Helper left partway")}
                  </label>
                  {partway ? (
                    <Input
                      disabled={busy}
                      value={alone}
                      onChange={(e) => setAlone(e.target.value.replace(/[^\d.]/g, ""))}
                      inputMode="decimal"
                      placeholder={unit === "GIRAH" ? t("How many girah alone?") : unit === "CM" ? t("How many cm alone?") : t("How many inches alone?")}
                      aria-label="Woven alone"
                    />
                  ) : null}
                </>
              ) : null}
              {odd ? (
                <div className="kr-odd" role="alert">
                  {oddText(odd)}
                  {oddKey && oddSeen === oddKey ? ` ${t("Tap Save again to keep it.")}` : ""}
                </div>
              ) : null}
              {rowErrors[requestId.current] ? (
                <div className="kr-pay is-missing" role="alert">
                  {t("Not saved")}: {rowErrors[requestId.current]}
                </div>
              ) : null}
              <PayBox calc={calc} inches={inches} sool={sool} />

              {/* A second part woven the same day: its own row, saved as its own entry with the same people. */}
              {extra.map((r, i) => {
                const o = oddFor(r.inches, r.unit, r.workTypeId);
                return (
                  <ExtraPartRow
                    key={r.key}
                    row={r}
                    n={i + 2}
                    types={visibleTypes}
                    taken={partTaken}
                    previewBody={bodyFor(r)}
                    odd={o ? `${oddText(o)}${oddKey && oddSeen === oddKey ? ` ${t("Tap Save again to keep it.")}` : ""}` : ""}
                    error={rowErrors[r.requestId]}
                    onChange={(patch) => patchRow(r.key, patch)}
                    onRemove={() => removeRow(r)}
                    disabled={busy}
                  />
                );
              })}
              {!filling ? (
                <button type="button" className="kr-addrow" disabled={busy} onClick={addRow}>
                  + {t("Add another part")}
                </button>
              ) : null}

              {entries.length ? (
                <>
                  <span className="kr-label">{t("Already entered for this day")}</span>
                  {entries.map((e: any) => (
                    <div key={e.id} className="kr-entry">
                      <span>
                        <strong>{e.workTypeCode}</strong> {e.inches} in · {e.weaverCode}
                        {e.ladkaSool ? ` · ${t("ladka sool")}` : ""}
                        {e.covering && e.enteredByName ? <span className="kw-muted"> · {e.enteredByName}</span> : null}
                        <CoveringTag entry={e} />
                        {e.status === "PENDING_APPROVAL" ? <span className="kw-chip is-review"> {t("waiting")}</span> : null}
                        {e.status === "REJECTED" ? <span className="kw-chip"> {t("rejected")}</span> : null}
                      </span>
                      <span>{e.calc?.total ? rupees(e.calc.total) : <span className="kw-chip is-review">{t("No approved rate")}</span>}</span>
                      {e.status !== "REJECTED" ? (
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => removeEntry(e)}>
                          {t("Remove")}
                        </button>
                      ) : null}
                    </div>
                  ))}
                </>
              ) : null}

              {/* History comes after today's entry: entering the day's work is the first job. */}
              {a.pieceId ? <SareeSoFar key={a.pieceId} pieceId={a.pieceId} sareeNumber={a.sareeNumber} reload={historyTick} addsLength={(id) => workTypes.some((w: any) => w.id === id && w.category === "SAREE")} /> : null}

              <details className="kr-extra">
                <summary>{t("More for this loom")}</summary>
                <div className="kr-extra__group">
                  <span className="kr-label">{t("This saree")}</span>
                  {filling ? (
                    // The saree isn't on the loom on this day yet: only what makes sense before its start.
                    <div className="kr-more">
                      <button
                        type="button"
                        className="kr-choice"
                        onClick={() => {
                          setForceNew(true);
                          setMode("start");
                        }}
                      >
                        {t("Start a different saree")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="kr-more">
                        {len?.ok ? (
                          <button type="button" className="kr-choice kr-choice--complete" disabled={busy} onClick={completeSaree}>
                            {t("Mark saree {{n}} complete", { n: a.sareeNumber })}
                          </button>
                        ) : (
                          // Locked until the saree is full length: it never looks done before it is.
                          <button type="button" className="kr-choice" disabled>
                            {t("Complete at {{full}} in · {{left}} in to go", { full: Math.round(len?.full || 0), left: Math.round(len?.left || 0) })}
                          </button>
                        )}
                        <button type="button" className="kr-choice" onClick={() => setMode("finish")}>{t("Stopped / cut")}</button>
                        <button type="button" className="kr-choice" onClick={() => setMode("start")}>{t("New saree")}</button>
                      </div>
                      {len && !len.ok ? (
                        <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setMode("check")}>
                          {t("Measured it full? Check length")}
                        </button>
                      ) : null}
                    </>
                  )}
                  {a.pieceId ? (
                    <div className="kr-extra__label">
                      <span className="kr-label">{t("HKP label")}</span>
                      <SareeLabelAttach key={a.pieceId} pieceId={a.pieceId} sareeNumber={a.sareeNumber} labelSerial={a.labelSerial} />
                    </div>
                  ) : null}
                </div>
                <div className="kr-extra__group">
                  <span className="kr-label">{t("This loom")}</span>
                  <div className="kr-more">
                    <button type="button" className="kr-choice" onClick={() => setMode("close")}>
                      {filling ? t("The loom was closed that day") : t("Close loom")}
                    </button>
                  </div>
                  <p className="kr-extra__links">
                    <Link to={kp.loom(loom.id)}>{t("All sarees on {{code}}", { code: loom.code })}</Link>
                  </p>
                </div>
              </details>
            </>
          ) : null}

          {mode === "start" && !a && loom.laterSaree && !forceNew ? (
            <div className="kr-later">
              <strong>
                {t("Saree {{n}} ({{design}}) is on this loom from {{day}}.", {
                  n: loom.laterSaree.sareeNumber,
                  design: loom.laterSaree.designNumber || "",
                  day: shortDay(offer?.currentStart || loom.laterSaree.from),
                })}
              </strong>
              {!offer?.canMove ? (
                <span className="kw-muted">{t("Ask the office to move saree {{n}}'s start", { n: loom.laterSaree.sareeNumber })}</span>
              ) : !offer.allowed ? (
                <span className="kh-since__problem" role="alert">
                  {t(offer.problem || "Its start can't move to this day")}
                </span>
              ) : (
                <span>{t("Move its start to {{day}}?", { day: shortDay(date) })}</span>
              )}
              {offer?.canMove ? (
                <Button className="ResetBtn" disabled={busy || !offer.allowed} onClick={moveStart}>
                  {t("Move start to {{day}}", { day: shortDay(date) })}
                  <small>
                    {t("Saree")} {loom.laterSaree.sareeNumber} · {loom.laterSaree.weaver?.name}
                  </small>
                </Button>
              ) : null}
              <Button outline disabled={busy} onClick={() => setForceNew(true)}>
                {t("Start a different saree")}
              </Button>
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setMode("close")}>
                {t("The loom was closed that day")}
              </button>
            </div>
          ) : null}

          {mode === "start" && (a || !loom.laterSaree || forceNew) ? (
            <>
              {mode === "start" ? (
                <>
                  <span className="kr-label">
                    {t("Saree number")} <span className="kw-muted">· {t("next number")}</span>
                  </span>
                  {typeNumber ? (
                    <Input
                      value={numberText}
                      onChange={(e) => setNumberText(e.target.value.replace(/[^\d]/g, ""))}
                      inputMode="numeric"
                      placeholder={nextSaree ? String(nextSaree) : ""}
                      aria-label={t("Saree number")}
                      className="kr-saree-input"
                    />
                  ) : (
                    <div className="kr-saree-no">
                      <span className="kw-code">{nextSaree ?? "…"}</span>
                      <span className="kw-muted">{t("next number, given when you start")}</span>
                    </div>
                  )}
                  <span className="kr-label">{t("On the loom since")}</span>
                  <div className="kr-chips" style={{ marginBottom: 4 }}>
                    <button type="button" className={`kr-choice${!earlier ? " is-on" : ""}`} onClick={() => { setEarlier(false); setStartOn(date); }}>
                      {fmtDay(date)}
                    </button>
                    <button type="button" className={`kr-choice${earlier ? " is-on" : ""}`} onClick={() => setEarlier(true)}>
                      {t("Earlier day…")}
                    </button>
                  </div>
                  {earlier ? (
                    <Input
                      type="date"
                      value={startOn}
                      max={date}
                      onChange={(e) => setStartOn(e.target.value || date)}
                      aria-label={t("On the loom since")}
                    />
                  ) : null}
                  <span className="kr-label">{t("Design")}</span>
                  <DesignPicker value={design} onChange={setDesign} autoFocus={false} />
                </>
              ) : null}
              <span className="kr-label">{t("Weaver")}</span>
              <PersonPicker
                value={weaver}
                onChange={setWeaver}
                placeholder={t("Code or name, e.g. K_56")}
                label={t("Weaver")}
                onAddNew={(name) => setAdding({ target: "weaver", name })}
                addLabel="as a new weaver"
              />
              <span className="kr-label">{t("Helper")}</span>
              <HelperModeChips value={startMode} onChange={setStartMode} />
              {!alonePerson ? (
                <PersonPicker
                  value={helper}
                  onChange={setHelper}
                  placeholder={t("Code or name, e.g. J_120")}
                  label={t("Helper")}
                  onAddNew={(name) => setAdding({ target: "helper", name })}
                  addLabel="as a new helper"
                />
              ) : null}
              {mode === "start" ? (
                <>
                  <span className="kr-label">
                    {date === todayIso() ? t("Length woven today") : t("Length woven on {{day}}", { day: fmtDay(date) })}{" "}
                    <span className="kw-muted">· {t("optional")}</span>
                  </span>
                  <div className="kr-inches">
                    <Input
                      value={startLength}
                      onChange={(e) => setStartLength(e.target.value.replace(/[^\d.]/g, ""))}
                      inputMode="decimal"
                      enterKeyHint="done"
                      placeholder="0"
                      aria-label={t("Length woven")}
                    />
                    <div className="kr-unit-toggle" role="radiogroup" aria-label="Unit">
                      {LENGTH_UNITS.map(({ unit: u, label }) => (
                        <button key={u} type="button" role="radio" aria-checked={startUnit === u} className={`kr-choice${startUnit === u ? " is-on" : ""}`} onClick={() => setStartUnit(u)}>
                          {t(label)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
              {mode === "start" && owner && design ? (
                <>
                  <span className="kr-label">
                    {t("Weaver's rate (₹ per {{unit}})", { unit: t(rateUnit === "GIRAH" ? "girah" : "inch") })}{" "}
                    <span className="kw-muted">· {rateFrom ? t("from the rate card") : t("optional, can be set later")}</span>
                  </span>
                  <div className="kr-inches">
                    <Input value={rate} onChange={(e) => setRate(e.target.value.replace(/[^\d./]/g, ""))} inputMode="decimal" placeholder={t("₹ e.g. 12")} aria-label={t("Weaver's rate")} />
                    <div className="kr-unit-toggle" role="radiogroup" aria-label="Unit">
                      {(["INCH", "GIRAH"] as const).map((u) => (
                        <button key={u} type="button" role="radio" aria-checked={rateUnit === u} className={`kr-choice${rateUnit === u ? " is-on" : ""}`} onClick={() => setRateUnit(u)}>
                          {u === "INCH" ? t("per inch") : t("per girah")}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
              <p className="kw-muted mt-2">{t("From {{day}}. Earlier days keep the people they had.", { day: fmtDay(mode === "start" ? startOn : date) })}</p>
              {mode === "start" && !a ? (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setMode("close")}>
                  {t("Loom not working? Mark it closed")}
                </button>
              ) : null}
            </>
          ) : null}

          {mode === "close" ? (
            <>
              <span className="kr-label">{t("Why is {{code}} closed?", { code: loom.code })}</span>
              <div className="kr-chips">
                {CLOSE_REASONS.map((r) => (
                  <button key={r.key} type="button" className={`kr-choice${reason === r.key ? " is-on" : ""}`} onClick={() => setReason(r.key)}>
                    {t(r.label)}
                  </button>
                ))}
              </div>
              <span className="kr-label">{t("Note (optional)")}</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
              <p className="kw-muted mt-2">{t("It stays closed every day until work is entered or it is reopened.")}</p>
            </>
          ) : null}

          {mode === "finish" ? (
            <>
              <span className="kr-label">{t("Saree {{n}} is", { n: a?.sareeNumber })}</span>
              <div className="kr-chips">
                {[["COMPLETED", "Completed"], ["STOPPED", "Stopped"], ["CUT", "Cut"]].map(([k, l]) => (
                  <button key={k} type="button" className={`kr-choice${finishAs === k ? " is-on" : ""}`} onClick={() => setFinishAs(k)}>
                    {t(l)}
                  </button>
                ))}
              </div>
              <p className="kw-muted mt-2">{t("On {{day}}. Work entered for it stays and is still paid.", { day: fmtDay(date) })}</p>
            </>
          ) : null}
        </div>

        <footer className="kr-sheet__foot">
          {mode === "start" && !a && loom.laterSaree && !forceNew ? null : mode === "entry" && a ? (
            <>
              <Button outline disabled={busy} onClick={() => save(false)}>
                {t("Save")}
              </Button>
              <Button className="ResetBtn" disabled={busy} onClick={() => save(true)}>
                {busy ? t("Saving…") : t("Save · next loom")}
              </Button>
            </>
          ) : (
            <>
              {(a && mode !== "entry") || (!a && mode !== "start") ? (
                <Button outline disabled={busy} onClick={() => setMode(a ? "entry" : "start")}>
                  {t("Back")}
                </Button>
              ) : null}
              <Button
                className="ResetBtn"
                disabled={busy}
                onClick={mode === "start" ? submitStart : mode === "close" ? submitClose : mode === "check" ? submitCheck : submitFinish}
              >
                {busy
                  ? t("Saving…")
                  : mode === "start"
                  ? `${t("Start saree")}${!numberText && nextSaree ? ` ${nextSaree}` : numberText ? ` ${numberText}` : ""}${
                      startLength.trim() ? ` · ${t("save")} ${startLength.trim()} ${t(LENGTH_UNITS.find((x) => x.unit === startUnit)?.label || "inch")}` : ""
                    }`
                  : mode === "close"
                  ? t("Close loom")
                  : mode === "check"
                  ? isFullLength ? t("Save length") : t("Check")
                  : t("Finish saree")}
              </Button>
            </>
          )}
        </footer>
        {adding ? (
          <QuickAddPerson
            initialName={adding.name}
            role={adding.target === "helper" || adding.target === "dayHelper" ? "HELPER" : "WEAVER"}
            title={adding.target === "helper" || adding.target === "dayHelper" ? t("New helper") : t("New weaver")}
            onCancel={() => setAdding(null)}
            onDone={(p) => {
              const person = { id: p.id, code: p.code, name: p.name };
              if (adding.target === "weaver") setWeaver(person);
              if (adding.target === "helper") setHelper(person);
              if (adding.target === "dayWeaver") pickDay("weaver", person);
              if (adding.target === "dayHelper") pickDay("helper", person);
              notifications({ title: t("Added"), message: `${p.code} ${p.name}`, type: "success" });
              setAdding(null);
            }}
          />
        ) : null}
        <Modal isOpen={geoOpen} toggle={() => setGeoOpen(false)} centered>
          <ModalHeader toggle={() => setGeoOpen(false)}>{t("Loom {{code}}: location and photo", { code: loom.code })}</ModalHeader>
          <ModalBody>
            <LoomRegister loom={loom} busy={busy} staff={staff} onTag={tagHere} onPhoto={addPhoto} onLink={linkRegister} />
          </ModalBody>
        </Modal>
        <Modal isOpen={!!shortBy} toggle={() => setShortBy(null)} centered>
          <ModalHeader toggle={() => setShortBy(null)}>{t("Not full length yet")}</ModalHeader>
          <ModalBody>
            {shortBy && a ? (
              <>
                <p>
                  {t("Saree {{n}} so far:", { n: a.sareeNumber })}{" "}
                  <strong>
                    {Math.round(shortBy.entered * 10) / 10} in · {gajGirah(shortBy.entered, Number(a.progress?.inchesPerGirah) || 2.6875)}
                  </strong>
                </p>
                <p>
                  {t("A full saree is about")}{" "}
                  <strong>
                    {Math.round(shortBy.full)} in · {gajGirah(shortBy.full, Number(a.progress?.inchesPerGirah) || 2.6875)}
                  </strong>
                  .
                </p>
                <p className="kw-muted mb-0">
                  {t("Don't mark it complete yet. Go and look at the loom again: an entry may be missing. If it was taken off early, use “Stopped / cut”.")}
                </p>
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <button type="button" className="kh-btn" onClick={() => { setShortBy(null); setMode("check"); }}>
              {t("Measure it")}
            </button>
            <button type="button" className="kh-btn is-primary" onClick={() => setShortBy(null)}>
              {t("I'll look again")}
            </button>
          </ModalFooter>
        </Modal>
      </section>
    </>
  );
};

export default LoomSheet;
